import classNames from "classnames";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

// See: https://mui.com/material-ui/react-list/

const ListItemButtonActiveColor = {
  primary500: "primary500",
} as const;

const ListItemButtonNormalColor = {
  neutral0: "neutral0",
  primary300: "primary300",
  primary800: "primary800",
} as const;

export const ListItemSize = {
  small: "small",
  medium: "medium",
  large: "large",
} as const;

const ListItemButtonColorConfig = {
  [ListItemButtonNormalColor.neutral0]: {
    [ListItemButtonActiveColor.primary500]: {
      button: {
        active: "bg-vpBlue-500 font-bold text-white",
        disabled: "cursor-default font-medium text-vpGray-500",
        normal: "bg-white font-normal hover:bg-vpBlue-500 hover:text-white",
      },
    },
  },
  [ListItemButtonNormalColor.primary300]: {
    [ListItemButtonActiveColor.primary500]: {
      button: {
        active: "bg-vpBlue-500 font-bold text-white",
        disabled: "cursor-default font-medium text-vpGray-500",
        normal: "bg-vpBlue-300 font-normal hover:bg-vpBlue-300 hover:text-white text-white",
      },
    },
  },
  [ListItemButtonNormalColor.primary800]: {
    [ListItemButtonActiveColor.primary500]: {
      button: {
        active: "bg-vpBlue-500 font-bold text-white",
        disabled: "cursor-default font-medium text-vpGray-500",
        normal: "bg-vpBlue-800 font-normal hover:bg-vpBlue-500 hover:text-white text-white",
      },
    },
  },
};

const ListItemSizeConfig = {
  [ListItemSize.small]: {
    root: "px-2 py-2 text-sm",
  },
  [ListItemSize.medium]: {
    root: "px-4 py-2 text-sm",
  },
  [ListItemSize.large]: {
    root: "px-6 py-3 text-md",
  },
} as const;

type ListItemRootNode = HTMLLIElement;
type ListItemOmits = "color";

export interface ListItemProps extends Omit<React.HTMLAttributes<ListItemRootNode>, ListItemOmits> {
  active?: boolean;
  color?: {
    active: keyof typeof ListItemButtonActiveColor;
    normal: keyof typeof ListItemButtonNormalColor;
  };
  dataCy?: string;
  disabled?: boolean;
  disableGutters?: boolean;
  endNode?: React.ReactNode;
  passive?: boolean;
  size?: keyof typeof ListItemSize;
  slots?: {
    button?: {
      css?: string;
    };
    children?: {
      css?: string;
    };
  };
  startNode?: React.ReactNode;
}

const ListItem = forwardRef<ListItemRootNode, ListItemProps>(
  (
    {
      active,
      children,
      className,
      color = {
        active: "primary500",
        normal: "neutral0",
      },
      dataCy,
      disabled,
      disableGutters,
      endNode,
      passive = false,
      size = ListItemSize.medium,
      slots,
      startNode,
      ...props
    },
    ref
  ) => {
    return (
      <li className={twMerge(classNames("w-full overflow-hidden", className))} data-cy={dataCy} ref={ref} {...props}>
        <div
          className={twMerge(
            classNames(
              "flex w-full items-center space-x-2",
              ListItemSizeConfig[size].root,
              {
                [ListItemButtonColorConfig[color.normal][color.active].button.normal]: !passive && !disabled,
                [ListItemButtonColorConfig[color.normal][color.active].button.active]: !passive && !disabled && active,
                [ListItemButtonColorConfig[color.normal][color.active].button.disabled]: !passive && disabled,
                "cursor-pointer": !passive && !disabled,
                "px-0": disableGutters,
              },
              slots?.button?.css
            )
          )}
        >
          {startNode && <span className="flex items-center">{startNode}</span>}
          <div className={twMerge(classNames("w-full", slots?.children?.css))}>{children}</div>
          {endNode && <span className="flex items-center">{endNode}</span>}
        </div>
      </li>
    );
  }
);

ListItem.displayName = ListItem.name;

export default ListItem;
